import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { 
  Upload, FileSpreadsheet, Calendar, Clock, CheckCircle2, XCircle, 
  AlertCircle, ChevronRight, ChevronDown, Play, Trash2, Eye,
  Filter, Search, RefreshCw, Users, MapPin, Loader2
} from 'lucide-react';
import {
  uploadSchedule,
  importSchedule,
  getScheduleUploads,
  getScheduleUpload,
  getShortRangeSchedule,
  getLongRangeSchedule,
  deleteScheduleUpload,
  getWorkOrderDrafts,
  activateWorkOrderDraft,
  rejectWorkOrderDraft,
  bulkActivateDrafts,
  getUsers
} from '../services/api';

const SchedulePage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('upload');
  const [uploads, setUploads] = useState([]);
  const [selectedUpload, setSelectedUpload] = useState(null);
  const [shortRangeData, setShortRangeData] = useState(null);
  const [longRangeData, setLongRangeData] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [selectedDrafts, setSelectedDrafts] = useState([]);
  const [expandedWeeks, setExpandedWeeks] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [crewLeaders, setCrewLeaders] = useState([]);
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [modalCrewLeaderId, setModalCrewLeaderId] = useState('');
  const [modalLocationName, setModalLocationName] = useState('');

  // Fetch schedule uploads
  const fetchUploads = useCallback(async () => {
    try {
      const res = await getScheduleUploads();
      setUploads(res.data);
      if (res.data.length > 0 && !selectedUpload) {
        setSelectedUpload(res.data[0]);
      }
    } catch (err) {
      console.error('Failed to fetch uploads:', err);
    }
  }, [selectedUpload]);

  // Fetch crew leaders
  const fetchCrewLeaders = useCallback(async () => {
    try {
      const res = await getUsers();
      setCrewLeaders(res.data.filter(u => u.role === 'crew_leader' || u.role === 'supervisor'));
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  }, []);

  useEffect(() => {
    fetchUploads();
    fetchCrewLeaders();
  }, [fetchUploads, fetchCrewLeaders]);

  // Fetch schedule data when upload is selected
  useEffect(() => {
    if (selectedUpload && selectedUpload.status === 'completed') {
      fetchScheduleData(selectedUpload.id);
    }
  }, [selectedUpload]);

  const fetchScheduleData = async (uploadId) => {
    setLoading(true);
    try {
      const [shortRes, longRes] = await Promise.all([
        getShortRangeSchedule(uploadId),
        getLongRangeSchedule(uploadId)
      ]);
      setShortRangeData(shortRes.data);
      setLongRangeData(longRes.data);
      
      // Initialize expanded weeks
      const expanded = {};
      Object.keys(shortRes.data.weeks || {}).forEach(week => {
        expanded[week] = true;
      });
      setExpandedWeeks(expanded);
    } catch (err) {
      console.error('Failed to fetch schedule data:', err);
      toast.error('Failed to load schedule data');
    } finally {
      setLoading(false);
    }
  };

  const fetchDrafts = useCallback(async () => {
    try {
      const params = {};
      if (selectedUpload) params.schedule_upload_id = selectedUpload.id;
      if (searchTerm) params.search = searchTerm;
      if (statusFilter) params.status = statusFilter;
      
      const res = await getWorkOrderDrafts(params);
      setDrafts(res.data.drafts || []);
    } catch (err) {
      console.error('Failed to fetch drafts:', err);
    }
  }, [selectedUpload, searchTerm, statusFilter]);

  useEffect(() => {
    if (activeTab === 'drafts') {
      fetchDrafts();
    }
  }, [activeTab, fetchDrafts]);

  // Handle file upload
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please upload an Excel file (.xlsx or .xls)');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await uploadSchedule(formData);
      toast.success('File uploaded successfully');
      setSelectedUpload(res.data);
      fetchUploads();
    } catch (err) {
      console.error('Upload failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  // Handle import
  const handleImport = async (sheets = null) => {
    if (!selectedUpload) return;

    setImporting(true);
    try {
      const res = await importSchedule(selectedUpload.id, sheets);
      toast.success(`Imported ${res.data.total_items} items, created ${res.data.total_drafts} drafts`);
      
      // Refresh data
      const uploadRes = await getScheduleUpload(selectedUpload.id);
      setSelectedUpload(uploadRes.data);
      fetchScheduleData(selectedUpload.id);
    } catch (err) {
      console.error('Import failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to import schedule');
    } finally {
      setImporting(false);
    }
  };

  // Handle draft activation
  const handleActivateDraft = async (draft, activationData = {}) => {
    try {
      const res = await activateWorkOrderDraft(draft.id, activationData);
      toast.success(`Work Order ${res.data.wo_number} created`);
      fetchScheduleData(selectedUpload.id);
      setShowActivateModal(false);
      setSelectedDraft(null);
    } catch (err) {
      console.error('Activation failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to activate draft');
    }
  };

  // Handle bulk activation
  const handleBulkActivate = async () => {
    if (selectedDrafts.length === 0) {
      toast.error('No drafts selected');
      return;
    }

    try {
      const res = await bulkActivateDrafts(selectedDrafts);
      toast.success(`Activated ${res.data.success_count} of ${res.data.total} drafts`);
      setSelectedDrafts([]);
      fetchScheduleData(selectedUpload.id);
    } catch (err) {
      console.error('Bulk activation failed:', err);
      toast.error('Failed to activate drafts');
    }
  };

  // Handle draft rejection
  const handleRejectDraft = async (draftId) => {
    try {
      await rejectWorkOrderDraft(draftId);
      toast.success('Draft rejected');
      fetchScheduleData(selectedUpload.id);
    } catch (err) {
      console.error('Rejection failed:', err);
      toast.error('Failed to reject draft');
    }
  };

  // Handle delete upload
  const handleDeleteUpload = async (uploadId) => {
    if (!window.confirm('Delete this schedule upload and all associated drafts?')) return;

    try {
      await deleteScheduleUpload(uploadId);
      toast.success('Schedule upload deleted');
      setSelectedUpload(null);
      fetchUploads();
    } catch (err) {
      console.error('Delete failed:', err);
      toast.error('Failed to delete upload');
    }
  };

  const toggleWeek = (week) => {
    setExpandedWeeks(prev => ({ ...prev, [week]: !prev[week] }));
  };

  const toggleDraftSelection = (draftId) => {
    setSelectedDrafts(prev => 
      prev.includes(draftId) 
        ? prev.filter(id => id !== draftId)
        : [...prev, draftId]
    );
  };

  const selectAllInWeek = (weekDrafts) => {
    const draftIds = weekDrafts.filter(d => d.status === 'draft').map(d => d.id);
    const allSelected = draftIds.every(id => selectedDrafts.includes(id));
    
    if (allSelected) {
      setSelectedDrafts(prev => prev.filter(id => !draftIds.includes(id)));
    } else {
      setSelectedDrafts(prev => [...new Set([...prev, ...draftIds])]);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: { color: 'bg-amber-500/20 text-amber-400', icon: Clock, label: 'Draft' },
      ready: { color: 'bg-blue-500/20 text-blue-400', icon: CheckCircle2, label: 'Ready' },
      activated: { color: 'bg-green-500/20 text-green-400', icon: CheckCircle2, label: 'Activated' },
      rejected: { color: 'bg-red-500/20 text-red-400', icon: XCircle, label: 'Rejected' }
    };
    const badge = badges[status] || badges.draft;
    const Icon = badge.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${badge.color}`}>
        <Icon className="w-3 h-3" />
        {badge.label}
      </span>
    );
  };

  // Upload Tab Content
  const renderUploadTab = () => (
    <div className="space-y-6">
      {/* Upload Section */}
      <div className="bg-slate-800 rounded-lg border border-slate-700 p-6">
        <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
          <Upload className="w-5 h-5 text-blue-400" />
          Upload Schedule Workbook
        </h3>
        
        <div className="border-2 border-dashed border-slate-600 rounded-lg p-8 text-center hover:border-blue-500 transition-colors">
          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileUpload}
            className="hidden"
            id="schedule-upload"
            disabled={uploading}
          />
          <label 
            htmlFor="schedule-upload" 
            className="cursor-pointer flex flex-col items-center gap-3"
          >
            {uploading ? (
              <Loader2 className="w-12 h-12 text-blue-400 animate-spin" />
            ) : (
              <FileSpreadsheet className="w-12 h-12 text-slate-400" />
            )}
            <div>
              <p className="text-slate-200 font-medium">
                {uploading ? 'Uploading...' : 'Click to upload Excel file'}
              </p>
              <p className="text-slate-400 text-sm mt-1">
                .xlsx or .xls files only
              </p>
            </div>
          </label>
        </div>
      </div>

      {/* Recent Uploads */}
      <div className="bg-slate-800 rounded-lg border border-slate-700 p-6">
        <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-blue-400" />
          Recent Uploads
        </h3>
        
        {uploads.length === 0 ? (
          <p className="text-slate-400 text-center py-8">No uploads yet</p>
        ) : (
          <div className="space-y-3">
            {uploads.map(upload => (
              <div 
                key={upload.id}
                className={`p-4 rounded-lg border transition-colors cursor-pointer ${
                  selectedUpload?.id === upload.id 
                    ? 'bg-blue-500/10 border-blue-500/50' 
                    : 'bg-slate-700/50 border-slate-600 hover:border-slate-500'
                }`}
                onClick={() => setSelectedUpload(upload)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileSpreadsheet className="w-8 h-8 text-green-400" />
                    <div>
                      <p className="text-slate-100 font-medium">{upload.filename}</p>
                      <p className="text-slate-400 text-sm">
                        {new Date(upload.uploaded_at).toLocaleString()} by {upload.uploaded_by_name}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {upload.status === 'completed' && (
                      <span className="text-sm text-slate-400">
                        {upload.total_items} items / {upload.total_drafts} drafts
                      </span>
                    )}
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      upload.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                      upload.status === 'processing' ? 'bg-blue-500/20 text-blue-400' :
                      upload.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                      'bg-amber-500/20 text-amber-400'
                    }`}>
                      {upload.status.toUpperCase()}
                    </span>
                    {user?.role === 'admin' && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteUpload(upload.id); }}
                        className="p-1 hover:bg-slate-600 rounded text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Import Section */}
      {selectedUpload && selectedUpload.status === 'pending' && (
        <div className="bg-slate-800 rounded-lg border border-slate-700 p-6">
          <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
            <Play className="w-5 h-5 text-green-400" />
            Import Schedule Data
          </h3>
          
          <p className="text-slate-400 mb-4">
            Select which weeks to import from the uploaded schedule:
          </p>
          
          <div className="flex flex-wrap gap-3 mb-6">
            <button
              onClick={() => handleImport(['T0', 'T1', 'T2', 'T3'])}
              disabled={importing}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white font-medium flex items-center gap-2 disabled:opacity-50"
            >
              {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calendar className="w-4 h-4" />}
              Import Short Range (T0-T3)
            </button>
            <button
              onClick={() => handleImport(['T4', 'T5', 'T6', 'T7', 'T8', 'T9', 'T10'])}
              disabled={importing}
              className="px-4 py-2 bg-slate-600 hover:bg-slate-500 rounded-lg text-white font-medium flex items-center gap-2 disabled:opacity-50"
            >
              {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calendar className="w-4 h-4" />}
              Import Long Range (T4+)
            </button>
            <button
              onClick={() => handleImport()}
              disabled={importing}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-white font-medium flex items-center gap-2 disabled:opacity-50"
            >
              {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
              Import All
            </button>
          </div>
        </div>
      )}
    </div>
  );

  // Short Range Tab Content
  const renderShortRangeTab = () => (
    <div className="space-y-4">
      {!selectedUpload ? (
        <div className="bg-slate-800 rounded-lg border border-slate-700 p-8 text-center">
          <AlertCircle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <p className="text-slate-300">Please select or upload a schedule first</p>
        </div>
      ) : loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
        </div>
      ) : shortRangeData ? (
        <>
          {/* Bulk Actions */}
          {selectedDrafts.length > 0 && (
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 flex items-center justify-between">
              <span className="text-blue-300">
                {selectedDrafts.length} draft(s) selected
              </span>
              <div className="flex gap-2">
                <button
                  onClick={handleBulkActivate}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-white font-medium text-sm flex items-center gap-2"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  Activate Selected
                </button>
                <button
                  onClick={() => setSelectedDrafts([])}
                  className="px-4 py-2 bg-slate-600 hover:bg-slate-500 rounded-lg text-white font-medium text-sm"
                >
                  Clear Selection
                </button>
              </div>
            </div>
          )}

          {/* Weeks */}
          {['T0', 'T1', 'T2', 'T3'].map(week => {
            const weekDrafts = shortRangeData.weeks[week] || [];
            const draftCount = weekDrafts.filter(d => d.status === 'draft').length;
            const activatedCount = weekDrafts.filter(d => d.status === 'activated').length;
            
            return (
              <div key={week} className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
                <div className="w-full p-4 flex items-center justify-between hover:bg-slate-700/50 transition-colors">
                  <button
                    onClick={() => toggleWeek(week)}
                    className="flex items-center gap-3 flex-1"
                  >
                    {expandedWeeks[week] ? (
                      <ChevronDown className="w-5 h-5 text-slate-400" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    )}
                    <span className="text-lg font-semibold text-slate-100">{week}</span>
                    <span className="text-sm text-slate-400">
                      ({weekDrafts.length} items)
                    </span>
                  </button>
                  <div className="flex items-center gap-3">
                    {draftCount > 0 && (
                      <span className="px-2 py-1 bg-amber-500/20 text-amber-400 rounded text-xs font-medium">
                        {draftCount} pending
                      </span>
                    )}
                    {activatedCount > 0 && (
                      <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs font-medium">
                        {activatedCount} activated
                      </span>
                    )}
                    {draftCount > 0 && (
                      <button
                        onClick={(e) => { e.stopPropagation(); selectAllInWeek(weekDrafts); }}
                        className="px-3 py-1 bg-slate-600 hover:bg-slate-500 rounded text-xs text-slate-200"
                      >
                        Select All
                      </button>
                    )}
                  </div>
                </div>
                
                {expandedWeeks[week] && weekDrafts.length > 0 && (
                  <div className="border-t border-slate-700 overflow-x-auto">
                    <table className="w-full min-w-[1100px]">
                      <thead className="bg-slate-700/50">
                        <tr>
                          <th className="w-10 p-3 sticky left-0 bg-slate-700/50"></th>
                          <th className="text-left p-3 text-xs font-medium text-slate-400 uppercase whitespace-nowrap">Activity</th>
                          <th className="text-left p-3 text-xs font-medium text-slate-400 uppercase min-w-[200px]">Description</th>
                          <th className="text-left p-3 text-xs font-medium text-slate-400 uppercase whitespace-nowrap">Area</th>
                          <th className="text-left p-3 text-xs font-medium text-slate-400 uppercase whitespace-nowrap">Division</th>
                          <th className="text-left p-3 text-xs font-medium text-slate-400 uppercase whitespace-nowrap">Equip No.</th>
                          <th className="text-left p-3 text-xs font-medium text-slate-400 uppercase whitespace-nowrap">Type</th>
                          <th className="text-left p-3 text-xs font-medium text-slate-400 uppercase whitespace-nowrap">FTE</th>
                          <th className="text-left p-3 text-xs font-medium text-slate-400 uppercase whitespace-nowrap">Status</th>
                          <th className="text-right p-3 text-xs font-medium text-slate-400 uppercase whitespace-nowrap sticky right-0 bg-slate-700/50">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-700">
                        {weekDrafts.map(draft => (
                          <tr key={draft.id} className="hover:bg-slate-700/30">
                            <td className="p-3 sticky left-0 bg-slate-800">
                              {draft.status === 'draft' && (
                                <input
                                  type="checkbox"
                                  checked={selectedDrafts.includes(draft.id)}
                                  onChange={() => toggleDraftSelection(draft.id)}
                                  className="rounded border-slate-500 text-blue-600 focus:ring-blue-500"
                                />
                              )}
                            </td>
                            <td className="p-3 whitespace-nowrap">
                              <span className="text-slate-200 font-mono text-sm">{draft.wo_number || '-'}</span>
                            </td>
                            <td className="p-3">
                              <span className="text-slate-300 text-sm line-clamp-2">{draft.title || '-'}</span>
                            </td>
                            <td className="p-3 whitespace-nowrap">
                              <span className="text-slate-400 text-sm">{draft.area || '-'}</span>
                            </td>
                            <td className="p-3 whitespace-nowrap">
                              <span className="text-slate-400 text-sm">{draft.division || '-'}</span>
                            </td>
                            <td className="p-3 whitespace-nowrap">
                              <span className="text-slate-400 text-sm">{draft.equip_no || '-'}</span>
                            </td>
                            <td className="p-3 whitespace-nowrap">
                              <span className="text-slate-400 text-sm">{draft.job_type || '-'}</span>
                            </td>
                            <td className="p-3">
                              <span className="text-slate-400 text-sm">{draft.fte || '-'}</span>
                            </td>
                            <td className="p-3 whitespace-nowrap">{getStatusBadge(draft.status)}</td>
                            <td className="p-3 text-right sticky right-0 bg-slate-800">
                              {draft.status === 'draft' && (
                                <div className="flex items-center justify-end gap-2">
                                  <button
                                    onClick={() => { 
                                      setSelectedDraft(draft); 
                                      setModalLocationName(draft.location_name || '');
                                      setModalCrewLeaderId('');
                                      setShowActivateModal(true); 
                                    }}
                                    className="p-1.5 bg-green-600 hover:bg-green-700 rounded text-white"
                                    title="Activate"
                                  >
                                    <CheckCircle2 className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => handleRejectDraft(draft.id)}
                                    className="p-1.5 bg-red-600 hover:bg-red-700 rounded text-white"
                                    title="Reject"
                                  >
                                    <XCircle className="w-4 h-4" />
                                  </button>
                                </div>
                              )}
                              {draft.status === 'activated' && (
                                <a
                                  href={`/work-orders/${draft.activated_work_order_id}`}
                                  className="p-1.5 bg-blue-600 hover:bg-blue-700 rounded text-white inline-flex"
                                  title="View Work Order"
                                >
                                  <Eye className="w-4 h-4" />
                                </a>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </>
      ) : (
        <div className="bg-slate-800 rounded-lg border border-slate-700 p-8 text-center">
          <AlertCircle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <p className="text-slate-300">No short range schedule data available</p>
          <p className="text-slate-400 text-sm mt-2">Import a schedule to see data here</p>
        </div>
      )}
    </div>
  );

  // Long Range Tab Content
  const renderLongRangeTab = () => (
    <div className="space-y-4">
      {!selectedUpload ? (
        <div className="bg-slate-800 rounded-lg border border-slate-700 p-8 text-center">
          <AlertCircle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <p className="text-slate-300">Please select or upload a schedule first</p>
        </div>
      ) : loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
        </div>
      ) : longRangeData && Object.keys(longRangeData.weeks || {}).length > 0 ? (
        <>
          {Object.entries(longRangeData.weeks).sort((a, b) => {
            const weekA = parseInt(a[0].replace('T', ''));
            const weekB = parseInt(b[0].replace('T', ''));
            return weekA - weekB;
          }).map(([week, weekDrafts]) => (
            <div key={week} className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
              <button
                onClick={() => toggleWeek(week)}
                className="w-full p-4 flex items-center justify-between hover:bg-slate-700/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  {expandedWeeks[week] ? (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  )}
                  <span className="text-lg font-semibold text-slate-100">{week}</span>
                  <span className="text-sm text-slate-400">
                    ({weekDrafts.length} items)
                  </span>
                </div>
              </button>
              
              {expandedWeeks[week] && weekDrafts.length > 0 && (
                <div className="border-t border-slate-700 overflow-x-auto">
                  <table className="w-full min-w-[1000px]">
                    <thead className="bg-slate-700/50">
                      <tr>
                        <th className="text-left p-3 text-xs font-medium text-slate-400 uppercase whitespace-nowrap">Activity</th>
                        <th className="text-left p-3 text-xs font-medium text-slate-400 uppercase min-w-[200px]">Description</th>
                        <th className="text-left p-3 text-xs font-medium text-slate-400 uppercase whitespace-nowrap">Area</th>
                        <th className="text-left p-3 text-xs font-medium text-slate-400 uppercase whitespace-nowrap">Division</th>
                        <th className="text-left p-3 text-xs font-medium text-slate-400 uppercase whitespace-nowrap">Equip No.</th>
                        <th className="text-left p-3 text-xs font-medium text-slate-400 uppercase whitespace-nowrap">Early Start</th>
                        <th className="text-left p-3 text-xs font-medium text-slate-400 uppercase whitespace-nowrap">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700">
                      {weekDrafts.map(draft => (
                        <tr key={draft.id} className="hover:bg-slate-700/30">
                          <td className="p-3 whitespace-nowrap">
                            <span className="text-slate-200 font-mono text-sm">{draft.wo_number || '-'}</span>
                          </td>
                          <td className="p-3">
                            <span className="text-slate-300 text-sm line-clamp-2">{draft.title || '-'}</span>
                          </td>
                          <td className="p-3 whitespace-nowrap">
                            <span className="text-slate-400 text-sm">{draft.area || '-'}</span>
                          </td>
                          <td className="p-3 whitespace-nowrap">
                            <span className="text-slate-400 text-sm">{draft.division || '-'}</span>
                          </td>
                          <td className="p-3 whitespace-nowrap">
                            <span className="text-slate-400 text-sm">{draft.equip_no || '-'}</span>
                          </td>
                          <td className="p-3 whitespace-nowrap">
                            <span className="text-slate-400 text-sm">
                              {draft.early_start ? new Date(draft.early_start).toLocaleDateString() : '-'}
                            </span>
                          </td>
                          <td className="p-3 whitespace-nowrap">{getStatusBadge(draft.status)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </>
      ) : (
        <div className="bg-slate-800 rounded-lg border border-slate-700 p-8 text-center">
          <AlertCircle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <p className="text-slate-300">No long range schedule data available</p>
          <p className="text-slate-400 text-sm mt-2">Import T4+ weeks to see data here</p>
        </div>
      )}
    </div>
  );

  // Activate Modal
  const renderActivateModal = () => {
    if (!showActivateModal || !selectedDraft) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-lg">
          <div className="p-6 border-b border-slate-700">
            <h3 className="text-xl font-semibold text-slate-100">Activate Work Order Draft</h3>
            <p className="text-slate-400 text-sm mt-1">
              Convert draft to a real work order
            </p>
          </div>
          
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Draft Details</label>
              <div className="bg-slate-700/50 rounded-lg p-4">
                <p className="text-slate-200 font-medium">{selectedDraft.title}</p>
                <p className="text-slate-400 text-sm mt-1">
                  Activity: {selectedDraft.wo_number || 'N/A'} | Area: {selectedDraft.area || 'N/A'}
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                <Users className="w-4 h-4 inline mr-1" />
                Assign Crew Leader
              </label>
              <select
                value={modalCrewLeaderId}
                onChange={(e) => setModalCrewLeaderId(e.target.value)}
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select crew leader (optional)</option>
                {crewLeaders.map(cl => (
                  <option key={cl.id} value={cl.id}>{cl.full_name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                <MapPin className="w-4 h-4 inline mr-1" />
                Location
              </label>
              <input
                type="text"
                value={modalLocationName}
                onChange={(e) => setModalLocationName(e.target.value)}
                placeholder="Enter location name"
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="p-6 border-t border-slate-700 flex justify-end gap-3">
            <button
              onClick={() => { setShowActivateModal(false); setSelectedDraft(null); setModalCrewLeaderId(''); setModalLocationName(''); }}
              className="px-4 py-2 bg-slate-600 hover:bg-slate-500 rounded-lg text-white font-medium"
            >
              Cancel
            </button>
            <button
              onClick={() => handleActivateDraft(selectedDraft, { 
                crew_leader_id: modalCrewLeaderId || undefined,
                location_name: modalLocationName || undefined 
              })}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-white font-medium flex items-center gap-2"
            >
              <CheckCircle2 className="w-4 h-4" />
              Activate
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Check permission
  if (!user || !['admin', 'supervisor'].includes(user.role)) {
    return (
      <div className="p-6">
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-6 text-center">
          <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-red-400">Access Denied</h3>
          <p className="text-slate-400 mt-2">Only Supervisors and Admins can access schedule management.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="schedule-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-100 flex items-center gap-3">
          <Calendar className="w-7 h-7 text-blue-400" />
          Schedule Management
        </h1>
        <p className="text-slate-400 mt-1">
          Upload weekly schedules and manage work order drafts
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-slate-700 pb-4">
        <button
          onClick={() => setActiveTab('upload')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'upload'
              ? 'bg-blue-600 text-white'
              : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
          }`}
          data-testid="tab-upload"
        >
          <Upload className="w-4 h-4" />
          Upload
        </button>
        <button
          onClick={() => setActiveTab('short-range')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'short-range'
              ? 'bg-blue-600 text-white'
              : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
          }`}
          data-testid="tab-short-range"
        >
          <Clock className="w-4 h-4" />
          Short Range (T0-T3)
        </button>
        <button
          onClick={() => setActiveTab('long-range')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'long-range'
              ? 'bg-blue-600 text-white'
              : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
          }`}
          data-testid="tab-long-range"
        >
          <Calendar className="w-4 h-4" />
          Long Range (T4+)
        </button>
      </div>

      {/* Selected Upload Info */}
      {selectedUpload && selectedUpload.status === 'completed' && (
        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileSpreadsheet className="w-5 h-5 text-green-400" />
            <div>
              <span className="text-slate-200 font-medium">{selectedUpload.filename}</span>
              <span className="text-slate-400 text-sm ml-3">
                {selectedUpload.total_items} items | {selectedUpload.total_drafts} drafts
              </span>
            </div>
          </div>
          <button
            onClick={() => fetchScheduleData(selectedUpload.id)}
            className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4 text-slate-400" />
          </button>
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'upload' && renderUploadTab()}
      {activeTab === 'short-range' && renderShortRangeTab()}
      {activeTab === 'long-range' && renderLongRangeTab()}

      {/* Activate Modal */}
      {renderActivateModal()}
    </div>
  );
};

export default SchedulePage;
